// umlaut-check: skip — "AenderungsZeitleiste"/"aenderungen" sind
// Komponenten- bzw. Prop-Namen, technische ASCII-Bezeichner.

import React from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {formatDatum, formatKw, formatMenge} from "./format.js";

/**
 * Deutsche Feldnamen für die Änderungshistorie (Sichtprüfung 1, Befund 19):
 * bislang standen hier die rohen Spaltennamen aus DATENMODELL.md
 * ("menge_zusage", "kw", "position" …).
 */
const FELD_LABEL = {
    kw: "Woche (Zuordnung)",
    kw_wunsch: "Woche (Wunsch)",
    kw_zusage: "Woche (Zusage)",
    kw_ist: "Woche (Ist)",
    bestelldatum: "Bestelldatum",
    versand_wunsch: "Versand Wunsch",
    versand_geplant: "Versand Geplant",
    versand_ist: "Versand Ist",
    rechnungsdatum: "Rechnungsdatum",
    marke: "Marke",
    zielort: "Zielort",
    incoterm: "Incoterm",
    cosmos_order_id: "Cosmos-Nr.",
    customer_order_reference: "Unsere Referenz",
    lkw_bezeichnung: "LKW-Bezeichnung",
    weclapp_bestellnummer: "Weclapp-Verweis",
    bemerkung: "Bemerkung",
    status: "Status",
    ampel: "Ampel",
    menge_wunsch: "Wunsch",
    menge_zusage: "Zusage",
    menge_ist: "Geliefert",
    menge_vorschlag_neu: "Neuer Vorschlag",
    bezeichnung: "Bezeichnung",
    cosmos_sku: "Cosmos-SKU",
    prioritaet: "Priorität",
    quelle: "Quelle",
    sicherheit: "Sicherheit",
    // "entfallen" auf Ebene der ganzen Lieferung schreibt eine Zeile mit
    // diesen beiden Kunstfeldern, weil "cosmos_aenderungen" keinen
    // Fremdschlüssel auf die gelöschte Position hat (siehe
    // Aenderungsprotokoll::entfernePosition()/entferneLieferung()).
    position: "Entfernte Menge",
    lieferung: "Lieferung",
};

/** Reine Mengenfelder - bekommen den deutschen Tausenderpunkt (Befund 19/24). */
const MENGENFELDER = new Set(["menge_wunsch", "menge_zusage", "menge_ist", "menge_vorschlag_neu", "position"]);

/** Status EINER POSITION ODER LIEFERUNG - eigenes Vokabular, nicht das der Prognose (format.js). */
const STATUS_LABEL = {
    geplant: "Geplant",
    bestaetigt: "Bestätigt",
    kommissionierung: "Kommissionierung",
    teilgeliefert: "Teilgeliefert",
    geliefert: "Geliefert",
    verschoben: "Verschoben",
};

/** "Wie belastbar die Angabe ist" - eigenes Vokabular für Lieferung/Position, nicht das der Prognose (format.js). */
const SICHERHEIT_LABEL = {
    zugesagt: "Zugesagt",
    geplant: "Geplant",
    vermutet: "Vermutet",
    ist: "Ist",
};

/** Ampel-Werte in der Historie - "weiss" heißt hier "nicht bewertet", nicht "Weiß" (AmpelSelect.jsx meint an anderer Stelle die Anzeige-Farbe). */
const AMPEL_LABEL = {
    gruen: "grün",
    gelb: "gelb",
    rot: "rot",
    rosa: "rosa",
    weiss: "nicht bewertet",
};

const QUELLE_TEXT = {
    excel: "aus Datei",
    mail: "aus E-Mail",
    hand: "von Hand",
    api: "aus Schnittstelle",
    weclapp: "aus Weclapp",
};

const ART_LABEL = {
    termin_verschoben: "Termin verschoben",
    menge_gekuerzt: "Menge gekürzt",
    menge_erhoeht: "Menge erhöht",
    status_gewechselt: "Status geändert",
    neu_aufgenommen: "Neu aufgenommen",
    entfallen: "Entfallen",
    feld_geaendert: "geändert",
    verhandlung: "Verhandlung mit Cosmos",
};

/** "status_gewechselt" trägt sowohl echte Statuswechsel als auch Ampelwechsel (Aenderungsprotokoll::protokolliere()) - am Feld unterscheiden. */
function artLabel(a) {
    if (a.art === "status_gewechselt" && a.feld === "ampel") {
        return "Ampel geändert";
    }
    return ART_LABEL[a.art] || a.art;
}

function formatWert(feld, wert) {
    if (wert === null || wert === undefined || wert === "") {
        return "–";
    }
    if (feld === "ampel") {
        return AMPEL_LABEL[wert] || wert;
    }
    if (feld === "status") {
        return STATUS_LABEL[wert] || wert;
    }
    if (feld === "sicherheit") {
        return SICHERHEIT_LABEL[wert] || wert;
    }
    if (feld === "quelle") {
        return QUELLE_TEXT[wert] || wert;
    }
    if (feld && feld.startsWith("kw")) {
        return formatKw(wert);
    }
    if (feld && MENGENFELDER.has(feld)) {
        return formatMenge(wert);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(wert))) {
        return formatDatum(wert);
    }
    return String(wert);
}

/**
 * "TT.MM.JJJJ HH:MM" ohne das Komma, das "toLocaleString('de-DE', …)"
 * zwischen Datum und Uhrzeit einfügt (Sichtprüfung 1, Befund 19/24).
 */
function formatZeitpunkt(iso) {
    if (!iso) {
        return "";
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
        return String(iso);
    }
    const zweistellig = (n) => String(n).padStart(2, "0");
    return `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.${d.getFullYear()} ${zweistellig(d.getHours())}:${zweistellig(d.getMinutes())}`;
}

const ZWISCHENSTAND_SUFFIX = ".zwischenstand";

/**
 * Fasst die zwei Protokollzeilen einer Verhandlung (Blatt "MOLOTOW (2)":
 * ursprünglich/nachgebessert/final, siehe
 * Aenderungsprotokoll::protokolliereVerhandlung()) zu einer Zeile zusammen,
 * wenn sie unmittelbar benachbart in der Liste stehen (so schreibt der
 * Service sie immer weg) - sonst bliebe "ursprünglich → final" und
 * "ursprünglich → nachgebessert" als zwei separate, schwer lesbare Zeilen
 * stehen (Sichtprüfung 1, Befund 19: "Verhandlung mit Cosmos: ursprünglich
 * → nachgebessert → final" als EIN Satz).
 */
function gruppiereVerhandlungen(liste) {
    const ergebnis = [];

    for (let i = 0; i < liste.length; i += 1) {
        const a = liste[i];
        if (a.art !== "verhandlung") {
            ergebnis.push(a);
            continue;
        }

        const istZwischenstand = String(a.feld).endsWith(ZWISCHENSTAND_SUFFIX);
        const basisKw = istZwischenstand ? a.feld.slice(0, -ZWISCHENSTAND_SUFFIX.length) : a.feld;
        const nachbar = liste[i + 1];
        const passtZusammen =
            nachbar &&
            nachbar.art === "verhandlung" &&
            nachbar.artikel_nummer === a.artikel_nummer &&
            (istZwischenstand ? nachbar.feld === basisKw : nachbar.feld === `${basisKw}${ZWISCHENSTAND_SUFFIX}`);

        if (passtZusammen) {
            const haupt = istZwischenstand ? nachbar : a;
            const zwischen = istZwischenstand ? a : nachbar;
            ergebnis.push({...haupt, _verhandlung: true, _kw: basisKw, _original: haupt.wert_alt, _final: haupt.wert_neu, _nachgebessert: zwischen.wert_neu});
            i += 1; // der Nachbar ist bereits mit verarbeitet
            continue;
        }

        ergebnis.push({
            ...a,
            _verhandlung: true,
            _kw: basisKw,
            _original: a.wert_alt,
            _final: istZwischenstand ? null : a.wert_neu,
            _nachgebessert: istZwischenstand ? a.wert_neu : null,
        });
    }

    return ergebnis;
}

/**
 * Änderungshistorie einer Lieferung, eines Farbtons (Artikel-Blatt) oder
 * einer Lieferplan-Zelle als Zeitleiste: wer, wann, was, alt -> neu,
 * Tage-Differenz - alles in Klartext (Sichtprüfung 1, Befund 19: bislang
 * standen hier rohe Feldnamen wie "menge_zusage" und "kw:", unformatierte
 * Zahlen und ein irreführendes "Status geändert" auch für Ampelwechsel).
 * Leere Historie zeigt keinen Platzhalter-Text, sondern verschwindet
 * vollständig (KONVENTIONEN.md Abschnitt 4).
 */
export default function AenderungsZeitleiste({aenderungen}) {
    if (!aenderungen || aenderungen.length === 0) {
        return null;
    }

    const zeilen = gruppiereVerhandlungen(aenderungen);

    return (
        <ul className="list-unstyled small mb-0">
            {zeilen.map((a) => (
                <li key={a.id} className="d-flex align-items-start gap-2 border-bottom py-2">
                    <span className="text-muted" style={{minWidth: 132}}>
                        {formatZeitpunkt(a.erfasst_am)}
                    </span>
                    <span className="badge text-bg-light border" style={{whiteSpace: "normal"}}>
                        {a._verhandlung ? ART_LABEL.verhandlung : artLabel(a)}
                    </span>
                    {/* N6 (Sichtprüfung 2, 22.09.2026): lange Inhalte (Bemerkungen,
                        Artikelbezeichnungen) umbrechen statt die Zeile zu sprengen -
                        "min-width: 0" ist nötig, weil ein Flex-Kind sonst nie unter
                        seine intrinsische Inhaltsbreite schrumpft. */}
                    <span className="flex-grow-1" style={{minWidth: 0, overflowWrap: "break-word"}}>
                        {a.artikel_nummer ? <span className="fw-semibold me-1">{a.artikel_nummer}</span> : null}
                        {a._verhandlung ? (
                            <>
                                <span className="text-muted me-1">{formatKw(a._kw)}:</span>
                                {formatMenge(a._original)}
                                {a._nachgebessert !== null && a._nachgebessert !== undefined && <> → {formatMenge(a._nachgebessert)}</>}
                                {a._final !== null && a._final !== undefined && <> → {formatMenge(a._final)}</>}
                            </>
                        ) : a.art === "neu_aufgenommen" ? (
                            formatWert(null, a.wert_neu)
                        ) : (
                            <>
                                <span className="text-muted me-1">{FELD_LABEL[a.feld] || a.feld}:</span>
                                {formatWert(a.feld, a.wert_alt)} → {formatWert(a.feld, a.wert_neu)}
                            </>
                        )}
                        {a.tage_verschiebung !== null && a.tage_verschiebung !== undefined ? (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id={`tage-${a.id}`}>
                                        {a.tage_verschiebung > 0
                                            ? `Um ${a.tage_verschiebung} Tage nach hinten verschoben.`
                                            : `Um ${Math.abs(a.tage_verschiebung)} Tage vorgezogen.`}
                                    </Tooltip>
                                }
                            >
                                <span className={`badge ms-2 ${a.tage_verschiebung > 0 ? "text-bg-warning" : "text-bg-success"}`}>
                                    {a.tage_verschiebung > 0 ? "+" : ""}
                                    {a.tage_verschiebung} Tage
                                </span>
                            </OverlayTrigger>
                        ) : null}
                    </span>
                    <span className="text-muted text-end" style={{minWidth: 130}}>
                        {a.quelle && <div>{QUELLE_TEXT[a.quelle] || a.quelle}</div>}
                        {a.benutzer && <div>{a.benutzer}</div>}
                    </span>
                </li>
            ))}
        </ul>
    );
}
